import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Clock, Sparkles } from "lucide-react";

/**
 * UpcomingAnimeBar (Vertical, Static Top 5)
 * - Mobile responsive: tighter spacing + smaller cover + better truncation on small screens.
 * - Only: cover + title + date (or "Soon").
 * - "See all" remains disabled for now.
 */
export default function UpcomingAnimeBar({
  items = [],
  title = "Upcoming",
  maxItems = 5,
  className = "",
  heightClass = "h-auto",
  seeAllTo = "/discover/upcoming",
  showSeeAll = true,
}) {
  /* -----------------------------
     Helpers (kept from your original)
  ------------------------------ */
  const normalizeDate = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;

    if (typeof raw === "number") {
      const ms = raw < 2e10 ? raw * 1000 : raw;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    if (typeof raw === "string") {
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    }

    return null;
  };

  const formatShort = (date) => {
    try {
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
    } catch {
      return "";
    }
  };

  const sanitizeTitle = (t) =>
    String(t || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "")
      .trim();

  const safeNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const getCover = (a, name) => {
    const direct =
      a?.PhotoUrl || a?.photo_url || a?.CoverImage || a?.coverImage || a?.image || null;

    if (direct && typeof direct === "string" && !direct.includes("example.com")) {
      if (direct.startsWith("http")) return direct;
      return direct.startsWith("/") ? direct : `/${direct.replace(/^\/+/, "")}`;
    }

    const s = sanitizeTitle(name);
    return s ? `/images/${s}.jpg` : null;
  };

  const upcoming = useMemo(() => {
    const now = new Date();

    const mapped = (Array.isArray(items) ? items : [])
      .map((a) => {
        const id = a?.AnimeID ?? a?.id ?? a?.anime_id;
        const name = a?.Title ?? a?.title ?? "Untitled";

        const rawDate =
          a?.ReleaseDate ?? a?.release_date ?? a?.NextAiring ?? a?.next_airing;
        const date = normalizeDate(rawDate);

        const cover = getCover(a, name);

        const rating = safeNum(a?.AverageRating ?? a?.avg_rating);
        const ratingCount = safeNum(
          a?.RatingCount ?? a?.rating_count ?? a?.NumRatings ?? a?.num_ratings
        );
        const favoritesCount = safeNum(
          a?.FavoritesCount ?? a?.favorites_count ?? a?.NumFavorites ?? a?.num_favorites
        );

        const status = String(a?.Status || a?.status || "").toUpperCase();

        return {
          raw: a,
          id,
          name,
          date,
          cover,
          status,
          rating,
          ratingCount,
          favoritesCount,
        };
      })
      .filter((x) => x.id != null && x.name && x.cover);

    const datedFuture = mapped
      .filter((x) => x.date && x.date.getTime() >= now.getTime())
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const statusFallback = mapped
      .filter((x) => ["UPCOMING", "NOT_YET_RELEASED", "ANNOUNCED"].includes(x.status))
      .slice(0)
      .sort((a, b) => {
        const aEng = a.ratingCount * 2 + a.favoritesCount * 3;
        const bEng = b.ratingCount * 2 + b.favoritesCount * 3;
        return bEng - aEng;
      });

    const freshFallback = mapped.slice().sort((a, b) => {
      const aEng = a.ratingCount * 2 + a.favoritesCount * 3;
      const bEng = b.ratingCount * 2 + b.favoritesCount * 3;
      if (aEng !== bEng) return bEng - aEng;

      const aDist = Math.abs(a.rating - 7);
      const bDist = Math.abs(b.rating - 7);
      if (aDist !== bDist) return aDist - bDist;

      return 0;
    });

    const base = datedFuture.length ? datedFuture : statusFallback.length ? statusFallback : freshFallback;

    const seen = new Set();
    const unique = [];
    for (const x of base) {
      if (seen.has(x.id)) continue;
      seen.add(x.id);
      unique.push(x);
      if (unique.length >= maxItems) break;
    }

    return unique;
  }, [items, maxItems]);

  if (!upcoming.length) return null;

  /* -----------------------------
     Badge helper (date / soon)
  ------------------------------ */
  const pickBadge = (x) => {
    if (x?.date) return { Icon: Clock, text: formatShort(x.date), tone: "text-white/65" };
    if (["UPCOMING", "ANNOUNCED", "NOT_YET_RELEASED"].includes(x.status))
      return { Icon: Sparkles, text: "Soon", tone: "text-white/65" };
    return { Icon: Sparkles, text: "Soon", tone: "text-white/65" };
  };

  /* -----------------------------
     Styles (mobile-first + responsive)
  ------------------------------ */
  const PANEL =
    "rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl shadow-[0_25px_90px_rgba(0,0,0,0.45)]";

  const HEADER_ICON =
    "h-9 w-9 rounded-2xl grid place-items-center bg-black/22 border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]";

  // Mobile: smaller padding + gap + cover; Desktop: roomier
  const ROW =
    "group relative flex items-center gap-3 sm:gap-4 rounded-2xl border border-white/10 bg-black/20 px-3 sm:px-4 py-3 sm:py-4 transition-colors hover:bg-white/[0.04] hover:border-white/18";

  const ROW_INSET_HL =
    "pointer-events-none absolute inset-0 rounded-2xl shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]";

  // Mobile: slightly smaller pill text and padding
  const META_PILL =
    "inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-2.5 sm:px-3 py-1 text-[11px] sm:text-[12px] text-white/60";

  return (
    <aside className={["w-full", className].join(" ")}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={HEADER_ICON}>
            <Calendar size={16} className="text-emerald-200/85" />
          </div>

          <div className="leading-tight min-w-0">
            <p className="text-sm font-semibold text-white tracking-tight truncate">
              {title}
            </p>
            <p className="text-[11px] tracking-[0.18em] uppercase text-white/45 truncate">
              release radar
            </p>
          </div>
        </div>

        {/* See all (disabled for now) */}
        {showSeeAll ? (
          <Link
            to={seeAllTo}
            onClick={(e) => e.preventDefault()}
            aria-disabled="true"
            className="
              inline-flex items-center gap-1
              rounded-xl px-2 py-1
              text-[12px] font-semibold
              text-white/30 border border-white/10 bg-white/[0.02]
              cursor-not-allowed pointer-events-none
              flex-shrink-0
            "
            style={{ textDecoration: "none" }}
            title="Coming soon"
          >
            <span className="hidden xs:inline">See all</span>
            <span className="xs:hidden">All</span>
            <ChevronRight size={14} className="opacity-60" />
          </Link>
        ) : null}
      </div>

      {/* Panel */}
      <div className={["mt-3", PANEL, "p-2 sm:p-3", heightClass].join(" ")}>
        {/* Static list (top 5) */}
        <div className="flex flex-col gap-2.5 sm:gap-3">
          {upcoming.slice(0, 5).map((x) => {
            const badge = pickBadge(x);

            return (
              <Link
                key={x.id}
                to={`/anime/${x.id}`}
                className={ROW}
                style={{ textDecoration: "none" }}
              >
                <div className={ROW_INSET_HL} />

                {/* Cover (smaller on mobile) */}
                <div className="relative h-12 w-12 sm:h-14 sm:w-14 rounded-2xl overflow-hidden border border-white/10 bg-black/30 flex-shrink-0">
                  <img
                    src={x.cover}
                    alt={x.name}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      const s = sanitizeTitle(x.name);
                      const fb = s ? `/images/${s}.jpg` : null;
                      if (fb && e.currentTarget.src !== window.location.origin + fb) {
                        e.currentTarget.src = fb;
                      }
                    }}
                  />
                </div>

                {/* Text */}
                <div className="min-w-0 flex-1">
                  {/* Mobile: slightly smaller title; still readable */}
                  <p className="text-[13px] sm:text-[14px] font-semibold text-white/90 truncate">
                    {x.name}
                  </p>

                  <div className="mt-1.5 sm:mt-2">
                    <span className={META_PILL}>
                      <badge.Icon size={14} className="text-white/45" />
                      <span className={badge.tone}>{badge.text}</span>
                    </span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );
}
